import React, { useState, useRef, useEffect } from 'react';
import { useTheme, FONTS } from '../../contexts/ThemeContext';
import './LanguageFontSelector.css';
import flagAR from '../../assets/imgs/flags/AR.png';
import flagGB from '../../assets/imgs/flags/GB.png';
import flagKU from '../../assets/imgs/flags/KU.png';
import flagTR from '../../assets/imgs/flags/TR.png';

const LanguageFontSelector = ({ onLanguageChange }) => {
  const { font, changeFont } = useTheme();
  const [isOpen, setIsOpen] = useState(false);
  const [activeTab, setActiveTab] = useState('language'); // 'language' or 'font'
  const [selectedLang, setSelectedLang] = useState(() => {
    return localStorage.getItem('selectedLanguage') || 'KU';
  });
  const dropdownRef = useRef(null);

  const languages = [
    { 
      code: 'EN', 
      label: 'English', 
      flag: flagGB,
      nativeNames: {
        EN: 'English',
        KU: 'ئینگلیزی',
        AR: 'الإنجليزية',
        TR: 'İngilizce'
      }
    },
    { 
      code: 'KU', 
      label: 'Kurdish', 
      flag: flagKU,
      nativeNames: {
        EN: 'Kurdish',
        KU: 'کوردی',
        AR: 'الكردية',
        TR: 'Kürtçe'
      }
    },
    { 
      code: 'AR', 
      label: 'Arabic', 
      flag: flagAR,
      nativeNames: {
        EN: 'Arabic',
        KU: 'عەرەبی',
        AR: 'العربية',
        TR: 'Arapça'
      }
    },
    { 
      code: 'TR', 
      label: 'Turkish', 
      flag: flagTR,
      nativeNames: {
        EN: 'Turkish',
        KU: 'تورکی',
        AR: 'التركية',
        TR: 'Türkçe'
      }
    },
  ];

  // Translations
  const translations = {
    EN: {
      language: 'Language',
      font: 'Font',
      selectFont: 'Select font',
      selectFontOption: (fontName) => `Select ${fontName} font`
    },
    KU: {
      language: 'زمان',
      font: 'فۆنت',
      selectFont: 'فۆنت هەڵبژێرە',
      selectFontOption: (fontName) => `فۆنتی ${fontName} هەڵبژێرە`
    },
    AR: {
      language: 'اللغة',
      font: 'الخط',
      selectFont: 'اختر الخط',
      selectFontOption: (fontName) => `اختر خط ${fontName}`
    },
    TR: {
      language: 'Dil',
      font: 'Yazı Tipi',
      selectFont: 'Yazı tipi seç',
      selectFontOption: (fontName) => `${fontName} yazı tipini seç`
    }
  };

  const t = translations[selectedLang] || translations.EN;

  // Get translated font name
  const getFontName = (fontKey) => {
    const fontOption = FONTS[fontKey];
    if (fontOption && fontOption.translations) {
      return fontOption.translations[selectedLang] || fontOption.name;
    }
    return fontOption?.name || fontKey;
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      document.addEventListener('touchstart', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('touchstart', handleClickOutside);
    };
  }, [isOpen]);

  const handleLanguageSelect = (lang) => {
    setSelectedLang(lang.code);
    localStorage.setItem('selectedLanguage', lang.code);
    setIsOpen(false);
    // Notify parent component of language change
    if (onLanguageChange) {
      onLanguageChange(lang.code);
    }
    // Dispatch custom event for other components
    window.dispatchEvent(new CustomEvent('languageChange', { detail: lang.code }));
  };

  const handleFontSelect = (fontKey) => {
    changeFont(fontKey);
  };

  const currentLang = languages.find(lang => lang.code === selectedLang) || languages[1];
  const currentFont = FONTS[font] || FONTS.customFont1;
  const currentFontName = getFontName(font);

  const getLanguageName = (lang) => {
    return lang.nativeNames[selectedLang] || lang.label;
  };

  return (
    <div className="language-font-selector" ref={dropdownRef}>
      <button
        className="language-font-selector-button"
        onClick={() => setIsOpen(!isOpen)}
        aria-label="Change language or font"
        aria-expanded={isOpen}
      >
        <img src={currentLang.flag} alt={`${currentLang.label} flag`} className="language-font-icon" />
        <span className="language-font-name">{currentLang.code}</span>
        <svg 
          className={`language-font-arrow ${isOpen ? 'open' : ''}`}
          width="16" 
          height="16" 
          viewBox="0 0 16 16" 
          fill="none"
        >
          <path 
            d="M4 6L8 10L12 6" 
            stroke="currentColor" 
            strokeWidth="2" 
            strokeLinecap="round" 
            strokeLinejoin="round"
          />
        </svg>
      </button>
      
      {isOpen && (
        <div className="language-font-dropdown">
          {/* Tabs */}
          <div className="language-font-tabs">
            <button
              className={`language-font-tab ${activeTab === 'language' ? 'active' : ''}`}
              onClick={() => setActiveTab('language')}
            >
              {t.language}
            </button>
            <button
              className={`language-font-tab ${activeTab === 'font' ? 'active' : ''}`}
              onClick={() => setActiveTab('font')}
            >
              {t.font}
            </button>
          </div>

          {/* Language Content */}
          {activeTab === 'language' && (
            <div className="language-font-content">
              {languages.map((lang) => (
                <button
                  key={lang.code}
                  className={`language-font-option ${selectedLang === lang.code ? 'active' : ''}`}
                  onClick={() => handleLanguageSelect(lang)}
                >
                  <img src={lang.flag} alt={`${lang.label} flag`} className="language-font-option-icon" />
                  <span className="language-font-option-name">{getLanguageName(lang)}</span>
                  {selectedLang === lang.code && (
                    <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                      <path 
                        d="M13 4L6 11L3 8" 
                        stroke="currentColor" 
                        strokeWidth="2" 
                        strokeLinecap="round" 
                        strokeLinejoin="round"
                      />
                    </svg>
                  )}
                </button>
              ))}
            </div>
          )}

          {/* Font Content */}
          {activeTab === 'font' && (
            <div className="language-font-content">
              {Object.entries(FONTS).map(([key, fontOption]) => {
                const translatedName = getFontName(key);
                return (
                  <button
                    key={key}
                    className={`language-font-option ${font === key ? 'active' : ''}`}
                    onClick={() => handleFontSelect(key)}
                    aria-label={t.selectFontOption(translatedName)}
                    style={{ fontFamily: fontOption.family }}
                  >
                    <span className="language-font-option-icon">{fontOption.icon}</span>
                    <span className="language-font-option-name">{translatedName}</span>
                    {font === key && (
                      <svg 
                        width="16" 
                        height="16" 
                        viewBox="0 0 16 16" 
                        fill="none"
                        className="language-font-check"
                      >
                        <path 
                          d="M13.3333 4L6 11.3333L2.66667 8" 
                          stroke="currentColor" 
                          strokeWidth="2" 
                          strokeLinecap="round" 
                          strokeLinejoin="round"
                        />
                      </svg>
                    )}
                  </button>
                );
              })}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default LanguageFontSelector;

